/**
 * User File model interfaces matching the User Files API specification
 * 
 * @remarks
 * These models correspond to the User Files API endpoints at `/api/user-files`
 * Supports various file types (PDF, Word, Excel, images, videos, etc.), 
 * soft delete, batch operations, and organization scoping.
 * 
 * @see USER_FILES_API_DOCUMENTATION.md for complete API documentation
 * @see USER_FILES_QUICK_REFERENCE.md for quick reference guide
 */

import { BaseEntity, UserRef, OrganizationRef } from './base';

/**
 * Core UserFile model matching API schema
 * 
 * @remarks
 * Represents a file with unpopulated references.
 * For populated version, use UserFilePopulated.
 */
export interface UserFile extends BaseEntity {
  /**
   * File name (1-255 characters)
   */
  fileName: string;

  /**
   * Organization ID this file belongs to
   */
  orgId: string;

  /**
   * File URL or URLs (string or array of strings)
   */
  urls: string;

  /**
   * Storage path in cloud storage
   */
  storagePath: string;

  /**
   * User ID who uploaded the file
   */
  userId: string;

  /**
   * MIME type of the file (e.g., 'application/pdf', 'image/jpeg')
   */
  fileType: string;

  /**
   * File size in bytes. null for files uploaded before this release.
   */
  fileSize?: number | null;

  /**
   * Soft delete flag
   * @default false
   */
  isDeleted: boolean;

  /**
   * Timestamp when file was soft deleted
   * Only present when isDeleted is true
   */
  deletedAt?: Date;
}

/**
 * UserFile with populated references
 * 
 * @remarks
 * Returned by most User Files API endpoints.
 * Includes populated organization and user details.
 */
export interface UserFilePopulated extends Omit<UserFile, 'orgId' | 'userId'> {
  /**
   * Populated organization reference
   */
  orgId: OrganizationRef;

  /**
   * Populated user reference
   */
  userId: UserRef;
}

/**
 * Data Transfer Object for creating a single file
 * 
 * @remarks
 * Used in POST /api/user-files
 */
export interface CreateUserFileDto {
  /**
   * File name (required, 1-255 characters)
   */
  fileName: string;

  /**
   * Organization ID (required, MongoDB ObjectId)
   */
  orgId: string;

  /**
   * File URL (required)
   */
  urls: string;

  /**
   * Storage path in cloud storage (required)
   */
  storagePath: string;

  /**
   * MIME type of the file (required)
   */
  fileType: string;

  /**
   * File size in bytes for storage usage tracking
   */
  fileSize?: number;
}

/**
 * Data Transfer Object for batch creating files
 * 
 * @remarks
 * Used in POST /api/user-files/batch
 */
export interface BatchCreateUserFileDto {
  /**
   * Array of file items to create
   * Minimum 1 item required
   */
  files: CreateUserFileDto[];
}

/**
 * Data Transfer Object for updating a file
 * 
 * @remarks
 * Used in PUT /api/user-files/:id
 * All fields are optional
 */
export interface UpdateUserFileDto {
  /**
   * Updated file name (optional, 1-255 characters)
   */
  fileName?: string;

  /**
   * Updated file URL (optional)
   */
  urls?: string;

  /**
   * Updated storage path (optional)
   */
  storagePath?: string;

  /**
   * Updated MIME type (optional)
   */
  fileType?: string;
}

/**
 * Query parameters for getting all files
 * 
 * @remarks
 * Used in GET /api/user-files
 */
export interface GetAllFilesQueryParams {
  /**
   * Page number for pagination
   * @default 1
   * @min 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   * @min 1
   * @max 100
   */
  limit?: number;

  /**
   * Filter by file name (partial match)
   */
  fileName?: string;

  /**
   * Filter by MIME type (partial match)
   */
  fileType?: string;

  /**
   * Filter by organization ID
   */
  orgId?: string;

  /**
   * Filter by start date (ISO 8601 string)
   */
  startDate?: string;

  /**
   * Filter by end date (ISO 8601 string)
   */
  endDate?: string;
}

/**
 * Query parameters for getting files by organization
 * 
 * @remarks
 * Used in GET /api/user-files/organization/:orgId
 */
export interface GetFilesByOrganizationQueryParams {
  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   */
  limit?: number;
}

/**
 * Query parameters for getting files by user
 * 
 * @remarks
 * Used in GET /api/user-files/user/:userId
 */
export interface GetFilesByUserQueryParams {
  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   */
  limit?: number;
}

/**
 * Query parameters for searching files
 * 
 * @remarks
 * Used in GET /api/user-files/search
 */
export interface SearchFilesQueryParams {
  /**
   * Search query (required)
   */
  q: string;

  /**
   * Organization ID to search within (required)
   */
  orgId: string;

  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   */
  limit?: number;
}

/**
 * Query parameters for getting files by type
 * 
 * @remarks
 * Used in GET /api/user-files/type/:fileType
 */
export interface GetFilesByTypeQueryParams {
  /**
   * Organization ID (required)
   */
  orgId: string;

  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 10
   */
  limit?: number;
}

/**
 * Pagination metadata for file lists
 */
export interface FilePagination {
  /**
   * Total number of items
   */
  total: number;

  /**
   * Current page number
   */
  page: number;

  /**
   * Items per page
   */
  limit: number;

  /**
   * Total number of pages
   */
  pages: number;
}

/**
 * File statistics for an organization
 */
export interface FileStats {
  /**
   * Total number of files (active + deleted)
   */
  totalFiles: number;

  /**
   * Number of active files
   */
  activeFiles: number;

  /**
   * Number of deleted files
   */
  deletedFiles: number;

  /**
   * Total number of users who uploaded files
   */
  totalUsers: number;

  /**
   * File count grouped by user
   */
  filesByUser: Array<{
    _id: string;
    count: number;
  }>;

  /**
   * File count grouped by type
   */
  filesByType: Array<{
    _id: string;
    count: number;
  }>;

  /**
   * Number of uploads in last 30 days
   */
  recentUploads: number;

  /**
   * Date of most recent upload
   */
  lastUploadDate: Date;

  /**
   * Total storage size in bytes
   */
  totalSize?: number;
}

/**
 * API response wrapper for single file creation
 */
export interface CreateFileResponse {
  status: 'success';
  message: 'File created successfully';
  data: {
    file: UserFilePopulated;
  };
}

/**
 * API response wrapper for batch file creation
 */
export interface BatchCreateFileResponse {
  status: 'success';
  message: string; // e.g., "5 files created successfully"
  data: {
    files: UserFilePopulated[];
    count: number;
  };
}

/**
 * API response wrapper for file list
 */
export interface FileListResponse {
  status: 'success';
  data: {
    files: UserFilePopulated[];
    pagination: FilePagination;
  };
}

/**
 * API response wrapper for single file retrieval
 */
export interface GetFileResponse {
  status: 'success';
  data: {
    file: UserFilePopulated;
  };
}

/**
 * API response wrapper for file update
 */
export interface UpdateFileResponse {
  status: 'success';
  message: 'File updated successfully';
  data: {
    file: UserFilePopulated;
  };
}

/**
 * API response wrapper for file deletion
 */
export interface DeleteFileResponse {
  status: 'success';
  message: 'File deleted successfully';
}

/**
 * API response wrapper for file restoration (admin only)
 */
export interface RestoreFileResponse {
  status: 'success';
  message: 'File restored successfully';
  data: {
    file: UserFilePopulated;
  };
}

/**
 * API response wrapper for file statistics
 */
export interface FileStatsResponse {
  status: 'success';
  data: {
    stats: FileStats;
  };
}

/**
 * Common MIME types for file validation
 */
export enum CommonMimeTypes {
  // Documents
  PDF = 'application/pdf',
  WORD_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  WORD_DOC = 'application/msword',
  EXCEL_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  EXCEL_XLS = 'application/vnd.ms-excel',
  POWERPOINT_PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  POWERPOINT_PPT = 'application/vnd.ms-powerpoint',
  
  // Images
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  SVG = 'image/svg+xml',
  WEBP = 'image/webp',
  
  // Videos
  MP4 = 'video/mp4',
  WEBM = 'video/webm',
  OGG = 'video/ogg',
  
  // Audio
  MP3 = 'audio/mpeg',
  WAV = 'audio/wav',
  OGG_AUDIO = 'audio/ogg',
  
  // Text
  PLAIN_TEXT = 'text/plain',
  CSV = 'text/csv',
  HTML = 'text/html',
  
  // Data
  JSON = 'application/json',
  XML = 'application/xml',
  
  // Archives
  ZIP = 'application/zip',
  RAR = 'application/x-rar-compressed',
  SEVEN_ZIP = 'application/x-7z-compressed'
}

/**
 * File type categories for grouping
 */
export enum FileCategory {
  DOCUMENT = 'document',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  ARCHIVE = 'archive',
  TEXT = 'text',
  OTHER = 'other'
}

/**
 * Helper function to determine file category from MIME type
 */
export function getFileCategoryFromMimeType(mimeType: string): FileCategory {
  if (mimeType.startsWith('image/')) {
    return FileCategory.IMAGE;
  }
  if (mimeType.startsWith('video/')) {
    return FileCategory.VIDEO;
  }
  if (mimeType.startsWith('audio/')) {
    return FileCategory.AUDIO;
  }
  if (mimeType.startsWith('text/')) {
    return FileCategory.TEXT;
  }
  if (
    mimeType.includes('pdf') ||
    mimeType.includes('word') ||
    mimeType.includes('excel') ||
    mimeType.includes('powerpoint') ||
    mimeType.includes('msword') ||
    mimeType.includes('ms-excel') ||
    mimeType.includes('ms-powerpoint') ||
    mimeType.includes('officedocument')
  ) {
    return FileCategory.DOCUMENT;
  }
  if (
    mimeType.includes('zip') ||
    mimeType.includes('rar') ||
    mimeType.includes('compressed')
  ) {
    return FileCategory.ARCHIVE;
  }
  return FileCategory.OTHER;
}

/**
 * Helper function to get file extension from filename
 */
export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot !== -1 ? fileName.substring(lastDot + 1).toLowerCase() : '';
}

/**
 * Helper function to validate MIME type format
 */
export function isValidMimeType(mimeType: string): boolean {
  // Basic MIME type validation: type/subtype
  const mimeTypeRegex = /^[a-zA-Z0-9]+\/[a-zA-Z0-9\-\+\.]+$/;
  return mimeTypeRegex.test(mimeType);
}
